import React from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import {
  Network,
  Lightbulb,
  ArrowRight,
  Tag,
  Calendar,
  Sparkles,
} from "lucide-react";
import { InsightConnection } from "./types";
import { UserTakeaway } from "../../../../api/userTakeaways";
import { cn } from "../../../../lib/utils";

interface InsightConnectionsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insight: UserTakeaway | null;
  connections: InsightConnection[];
  onViewInsight: (id: string) => void;
}

const InsightConnectionsPanel: React.FC<InsightConnectionsPanelProps> = ({
  open,
  onOpenChange,
  insight,
  connections,
  onViewInsight,
}) => {
  const { t } = useTranslation();

  const getConnectionTypeIcon = (type: InsightConnection["connectionType"]) => {
    switch (type) {
      case "semantic":
        return <Sparkles className="h-3 w-3" />;
      case "category":
        return <Tag className="h-3 w-3" />;
      case "time":
        return <Calendar className="h-3 w-3" />;
      default:
        return <Network className="h-3 w-3" />;
    }
  };

  const getConnectionTypeLabel = (type: InsightConnection["connectionType"]) => {
    switch (type) {
      case "semantic":
        return t("similar_meaning") || "Similar meaning";
      case "category":
        return t("same_category") || "Same category";
      case "time":
        return t("captured_together") || "Captured together";
      default:
        return t("related") || "Related";
    }
  };

  const getConnectionColor = (similarity: number) => {
    if (similarity >= 0.9) return "bg-green-500";
    if (similarity >= 0.7) return "bg-amber-500";
    return "bg-primary";
  };

  if (!insight) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Network className="h-5 w-5 text-amber-500" />
            {t("connected_insights") || "Connected Insights"}
          </SheetTitle>
          <SheetDescription>
            {t("connections_description") ||
              "These insights are related based on content, category, or context."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Current Insight */}
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="h-4 w-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-amber-600 font-medium mb-1">
                  {t("current_insight") || "Current Insight"}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                  {insight.content}
                </p>
                {insight.category && (
                  <Badge
                    variant="outline"
                    className="mt-2 text-xs"
                    style={{ borderColor: insight.category.color }}
                  >
                    <div
                      className="w-2 h-2 rounded-full mr-1"
                      style={{ backgroundColor: insight.category.color }}
                    />
                    {insight.category.name}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Connection Line */}
          <div className="flex items-center justify-center">
            <div className="h-8 w-px bg-gradient-to-b from-amber-200 to-gray-200" />
          </div>

          {/* Connected Insights */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">
              {connections.length} {t("related_insights") || "Related Insights"}
            </p>

            {connections.length === 0 ? (
              <div className="text-center py-8">
                <Network className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {t("no_connections_yet") ||
                    "No connections found yet. As you add more insights, connections will appear automatically."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {connections.map((connection) => (
                  <button
                    key={connection.relatedInsightId}
                    onClick={() => onViewInsight(connection.relatedInsightId)}
                    className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      {/* Similarity indicator */}
                      <div className="flex-shrink-0 pt-1">
                        <div className="relative w-10 h-10">
                          <svg className="w-10 h-10 -rotate-90">
                            <circle
                              cx="20"
                              cy="20"
                              r="16"
                              fill="none"
                              stroke="#e5e7eb"
                              strokeWidth="3"
                            />
                            <circle
                              cx="20"
                              cy="20"
                              r="16"
                              fill="none"
                              stroke={
                                connection.similarity >= 0.9
                                  ? "#22c55e"
                                  : connection.similarity >= 0.7
                                  ? "#f59e0b"
                                  : "#3b82f6"
                              }
                              strokeWidth="3"
                              strokeDasharray={`${connection.similarity * 100} 100`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                            {Math.round(connection.similarity * 100)}%
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 leading-relaxed line-clamp-2 group-hover:text-gray-900">
                          {connection.insight?.content || t("loading") + "..."}
                        </p>

                        {/* Connection type */}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge
                            variant="secondary"
                            className="text-xs bg-gray-100"
                          >
                            {getConnectionTypeIcon(connection.connectionType)}
                            <span className="ml-1">
                              {getConnectionTypeLabel(connection.connectionType)}
                            </span>
                          </Badge>
                        </div>
                      </div>

                      {/* Arrow */}
                      <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* AI Suggestion */}
          {connections.length > 2 && (
            <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-violet-900 mb-1">
                    {t("ai_observation") || "AI Observation"}
                  </p>
                  <p className="text-xs text-violet-700 leading-relaxed">
                    {t("connection_pattern_detected") ||
                      "You have multiple insights around this theme. Consider creating a summary or action item to consolidate your learnings."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InsightConnectionsPanel;
